/*
Fork addition: data-driven storage detection, shared by the recipe
suggestion menu, the live search "Storage:" group and the place menu.

A factory counts as storage when its label (or, on unpackaged data, its
name) contains the word "storage" — the game's own naming for the
loose / unit / fluid / molten / virtual storage families and the
radioactive waste storage. A product is storable in a storage factory
when one of the factory's recipes takes (or returns) the product on a
non-hidden IO, matching either exactly or through the abstract product
class of the same GameItemType — the same matching the model itself
uses for links (see RecipeModelImpl._$isSimilarIo and Filter).

Special / energy products (electricity, workers, maintenance, ...)
never match through the abstract class: their type is shared with the
"Any Virtual Product" class, but they are not goods a storage holds.
They can still match by exact name if a recipe literally lists them
on a visible IO.
*/
import {GameItemType, GameRecipeIOFlags} from '#types/constants';
import type {GameItem, GameRecipe, GameRecipeIO} from '#types/game-data';
import type {GameData} from './data';

export function isStorageFactory(item: GameItem): boolean {
    return /\bstorage\b/i.test(item.label || '')
        || /\bstorage\b/i.test(item.name || '');
}

export function ioMatchesProduct(io: GameRecipeIO, product: GameItem): boolean {
    if(io.flags & GameRecipeIOFlags.HideInMenu)
        return false;
    const ioProduct = io.product;
    if(!ioProduct)
        return false;
    if(ioProduct.name === product.name)
        return true;
    if(!ioProduct.isAbstractClassItem)
        return false;
    const type = product.type;
    if((type === undefined)
        || (type === GameItemType.Unknown)
        || (type === GameItemType.Special)
        || (type === GameItemType.Energy))
        return false;
    return ioProduct.type === type;
}

export function recipeAcceptsInput(recipe: GameRecipe, product: GameItem): boolean {
    return recipe.input.some((io) => ioMatchesProduct(io, product));
}

export function recipeProducesOutput(recipe: GameRecipe, product: GameItem): boolean {
    return recipe.output.some((io) => ioMatchesProduct(io, product));
}

export interface StorageMatch {
    factory: GameItem;
    //best recipe for the requested direction: a recipe flowing both
    //ways (InOut) wins over a one-way recipe, so a placed storage can
    //be filled and emptied again without a recipe change
    recipe: GameRecipe;
    acceptsInput: boolean;
    producesOutput: boolean;
}

//storage factories able to store the product. direction 'in' = the
//storage takes the product (a consumer for it), 'out' = the storage
//hands the product out (a producer of it), 'any' = either direction.
export function findStorageMatches(
    gameData: GameData,
    product: GameItem,
    direction: 'in' | 'out' | 'any',
): StorageMatch[] {
    const matches: StorageMatch[] = [];
    for(const factory of gameData.gameFactoriesArray) {
        if(!isStorageFactory(factory))
            continue;
        const dictionary = factory.recipeDictionary;
        if(!dictionary)
            continue;
        let best: GameRecipe | undefined;
        let bestScore = -1;
        let acceptsInput = false;
        let producesOutput = false;
        for(const recipe of dictionary.recipes) {
            const inMatch = recipeAcceptsInput(recipe, product);
            const outMatch = recipeProducesOutput(recipe, product);
            acceptsInput = acceptsInput || inMatch;
            producesOutput = producesOutput || outMatch;
            const relevant = (direction === 'in') ? inMatch
                : (direction === 'out') ? outMatch
                    : (inMatch || outMatch);
            if(!relevant)
                continue;
            //both-ways recipes beat one-way recipes for this direction
            const score = (inMatch && outMatch) ? 2 : 1;
            if(score > bestScore) {
                bestScore = score;
                best = recipe;
            }
        }
        if(best)
            matches.push({factory, recipe: best, acceptsInput, producesOutput});
    }
    return matches;
}
